import Title_ru_RU_Class from "@/core/misc/decorators/Title_ru_RU_Class";
import Decimal from 'decimal.js';
import Numeric from '@/core/types/Numeric';
import Price   from '@/core/types/Price';
import Sum     from '@/core/types/Sum';
import NumberUtils from '@/core/utils/NumberUtils';
import type Unit from '@/domains/Units/Unit';
import {UnwrapNestedRefs} from "vue";

@Title_ru_RU_Class('Количество')
class Qty extends Numeric {
    protected static readonly SCALE = 3;

    static fromServer(value: string): Qty {
        return new Qty(value);
    }

    /** Результат вычисления — уже посчитанный Decimal. */
    static fromOperation(value: Decimal): Qty {
        return new Qty(value);
    }

    /** Количество + количество = количество. */
    plus(other: Qty | null | undefined): Qty {
        return Qty.fromOperation(this.value.plus(Qty.toDecimal(other)));
    }

    /** Количество − количество = количество. */
    minus(other: Qty | null | undefined): Qty {
        return Qty.fromOperation(this.value.minus(Qty.toDecimal(other)));
    }

    /**
     * Количество × цена = сумма.
     * Результат рождается типом Sum, который сам округлит до 2 знаков.
     */
    mul(price: Price | null | undefined): Sum {
        return Sum.fromOperation(this.value.times(Qty.toDecimal(price)));
    }

    /**
     * Человекочитаемое представление количества: число + единица измерения.
     * У количества нет валюты (в отличие от Sum/Price) — только единица.
     * Ядро форматирования числа — общий NumberUtils.formatDecimal; единица
     * клеится постфиксом неразрывным пробелом ( ), чтобы число и имя
     * единицы не рвались по разным строкам.
     * Пустое (не введённое) значение печатается пустой строкой, а не "NaN".
     *
     * @param unit - единица измерения (имя клеится постфиксом; null — без единицы)
     */
    format(unit: Unit | UnwrapNestedRefs<Unit> | null | undefined = null): string {
        if (this.isEmpty()) {
            return '';
        }

        let result = NumberUtils.formatDecimal(this.value, this.scale);

        if (unit) {
            result += ` ${unit.name}`;
        }

        return result;
    }

    /**
     * Целочисленное представление количества: без дробной части и без
     * разделителей разрядов. Ходовой кейс — счётные сущности (паллетоместа,
     * штуки): «10 шт», «5 шт». Дробный хвост срезается округлением (страховка,
     * по смыслу такое количество уже целое). Единица клеится постфиксом
     * неразрывным пробелом, как в format; null — голое число.
     * Пустое (не введённое) значение печатается пустой строкой, а не "NaN".
     *
     * @param unit - единица измерения (имя клеится постфиксом; null — без единицы)
     */
    formatAsInt(unit: Unit | null = null): string {
        if (this.isEmpty()) {
            return '';
        }

        let result = this.value.toFixed(0);

        if (unit) {
            result += ` ${unit.name}`;
        }

        return result;
    }

    /**
     * Количество с единицей измерения: "10 шт".
     * Имя единицы клеится постфиксом неразрывным пробелом (\u{00A0}), чтобы число
     * и единица не разрывались по строкам. Зеркально Qty.format(unit).
     *
     * Пустое значение печатается пустой строкой вместе с единицей: единица без
     * числа смысла не имеет.
     *
     * @param ?Unit $unit единица измерения; null - голое число
     */
    public formatSmart(unit: Unit|null = null): string
    {
        if (this.isEmpty()) {
            return '';
        }

        let result = super.formatSmart();

        if (result !== '' && unit !== null)
        {
            result += "\u{00A0}" + unit.name;
        }

        return result;
    }

    /**
     * Сокращённое количество с единицей измерения: "12,5 тыс шт", "3,45 млн кг".
     * Единица клеится постфиксом неразрывным пробелом — зеркально formatSmart.
     *
     * Пустое значение печатается пустой строкой вместе с единицей: единица без
     * числа смысла не имеет.
     *
     * @param unit - единица измерения; null — голое число
     */
    public formatShorten(unit: Unit|null = null): string
    {
        if (this.isEmpty()) {
            return '';
        }

        let result = super.formatShorten();

        if (result !== '' && unit !== null)
        {
            result += "\u{00A0}" + unit.name;
        }

        return result;
    }
}

export default Qty;